import { useMemo } from 'react';

import {
  useDoesProductFit,
  useMyAccount,
  useMyStore,
  useProductQuantity,
  useQuery
} from '../hooks';

import {
  COMPARE_STAGGERED_PRODUCT_QUERY,
  COMPARE_STANDARD_PRODUCT_QUERY
} from '../../graphql/product-compare-queries';

import { getVehicleInput } from '../../utils/fitment/fitment';
import {
  getAverageReviewRating,
  getNumberOfProductReviews
} from '../../utils/product/product';
import {
  getTireCostPerMile,
  getTreadwellGQLInput,
  getTreadwellSelectorType
} from '../../utils/treadwell/treadwell';
import { isArrayWithLength } from '../../utils/validators/validators';

import { BundleType } from '../../types/graphql-types';
import { DONT_FIT, EXCLUDED, FIT } from '../../constants/fit';
import { PRODUCT_COMPARE_SELECTOR_TYPE } from '../../constants/products';
import { TREADWELL_DEFAULT_PERSONA_NAME } from '../../constants/treadwell';

const RIDECOMFORT_KEY = 'ridecomfort';
const CORNERINGSTEERING_KEY = 'corneringsteering';
const RIDENOISE_KEY = 'ridenoise';
const TREADLIFE_KEY = 'treadlife';
const DRYTRACTION_KEY = 'drytraction';
const WETTRACTION_KEY = 'wettraction';
const WINTERWEATHER_KEY = 'winterweather';

function getRimWidthRangesDisplay(range) {
  const [low, high] = range?.split(' - ') || [];

  return `${low}" - ${high}"`;
}

function filterProductData(
  products,
  reviews,
  numOfProductsToDisplay,
  isStaggeredSearch
) {
  return products?.reduce(
    (acc, product = {}, idx) => {
      if (idx >= numOfProductsToDisplay) {
        return acc;
      }

      const newAcc = { ...acc };
      const { rearProduct, recommendationData, tdsData } = product || {};

      const {
        mileage = {},
        milesPerDollar,
        rideRating = {},
        stopping = {},
        tireLifeMonths,
        tireLifeYears,
        winter = {}
      } = recommendationData || tdsData || {};

      if (isStaggeredSearch && rearProduct) {
        newAcc.rimWidthRanges.push(
          product.widthRange
            ? `${getRimWidthRangesDisplay(
                product.widthRange
              )} / ${getRimWidthRangesDisplay(rearProduct.widthRange)}`
            : undefined
        );
        newAcc.productWeight.push(
          product.grossWeight
            ? `${product.grossWeight} lbs / ${rearProduct.grossWeight} lbs`
            : undefined
        );
        newAcc.aspectRatios.push(
          product.tireAspectRatio
            ? `${product.tireAspectRatio} / ${rearProduct.tireAspectRatio}`
            : undefined
        );
        newAcc.sectionWidths.push(
          product.tireCrossSection
            ? `${product.tireCrossSection} / ${rearProduct.tireCrossSection}`
            : undefined
        );
        newAcc.diameters.push(
          product.tireDiameter
            ? `${product.tireDiameter}" / ${rearProduct.tireDiameter}"`
            : undefined
        );
        newAcc.wheelDiameters.push(
          product.tireWheelRimDiameter
            ? `${product.tireWheelRimDiameter}" / ${rearProduct.tireWheelRimDiameter}"`
            : undefined
        );

        newAcc.treadGrades.push(
          product.treadGrade
            ? `${product.treadGrade} / ${rearProduct.treadGrade}`
            : undefined
        );
        newAcc.tractionGrades.push(
          product.tractionGrade
            ? `${product.tractionGrade} / ${rearProduct.tractionGrade}`
            : undefined
        );
        newAcc.tempGrades.push(
          product.tempGrade
            ? `${product.tempGrade} / ${rearProduct.tempGrade}`
            : undefined
        );
        newAcc.loadIndexes.push(
          product.loadIndex
            ? `${product.loadIndex} / ${rearProduct.loadIndex}`
            : undefined
        );
        newAcc.treadDepths.push(
          product.treadDepth
            ? `${product.treadDepth}/32" / ${rearProduct.treadDepth}/32"`
            : undefined
        );
        newAcc.loadRanges.push(
          product.loadRange
            ? `${product.loadRange} / ${rearProduct.loadRange}`
            : undefined
        );
        newAcc.maxPSIs.push(
          product.maxAirPressure
            ? `${product.maxAirPressure} psi / ${rearProduct.maxAirPressure} psi`
            : undefined
        );
        newAcc.speedRatings.push(
          product.speedRating
            ? `${product.speedRating} / ${rearProduct.speedRating}`
            : undefined
        );
        newAcc.sideWallDescriptions.push(
          product.sideWallDescriptionName
            ? `${product.sideWallDescriptionName} / ${rearProduct.sideWallDescriptionName}`
            : undefined
        );
        newAcc.warranties.push(
          product.mileageWarranty
            ? `${product.mileageWarranty} mi / ${rearProduct.mileageWarranty} mi`
            : undefined
        );
      } else {
        newAcc.rimWidthRanges.push(
          getRimWidthRangesDisplay(product.widthRange)
        );
        newAcc.productWeight.push(
          product.grossWeight ? `${product.grossWeight} lbs` : undefined
        );
        newAcc.aspectRatios.push(product.tireAspectRatio);
        newAcc.sectionWidths.push(product.tireCrossSection);
        newAcc.diameters.push(
          product.tireDiameter ? `${product.tireDiameter}"` : undefined
        );
        newAcc.wheelDiameters.push(
          product.tireWheelRimDiameter
            ? `${product.tireWheelRimDiameter}"`
            : undefined
        );

        newAcc.treadGrades.push(product.treadGrade);
        newAcc.tractionGrades.push(product.tractionGrade);
        newAcc.tempGrades.push(product.tempGrade);
        newAcc.treadDepths.push(
          product.treadDepth ? `${product.treadDepth}/32"` : undefined
        );
        newAcc.maxPSIs.push(
          product.maxAirPressure ? `${product.maxAirPressure} psi` : undefined
        );
        newAcc.speedRatings.push(product.speedRating);
        newAcc.warranties.push(
          product.mileageWarranty ? `${product.mileageWarranty} mi` : undefined
        );
        newAcc.loadIndexes.push(product.loadIndex);
        newAcc.loadRanges.push(product.loadRange);
        newAcc.sideWallDescriptions.push(product.sideWallDescriptionName);
      }

      newAcc.maxLoads.push(product.loadCapacity);
      newAcc.productNums.push(product.code);
      newAcc.icons.push(product.icons);
      newAcc.gbbs.push(product.gbb);
      newAcc.treadwellWarranties.push(mileage.warranty);

      newAcc.handlings.push(rideRating.handling);
      newAcc.wetHandlings.push(rideRating.wetHandling);
      newAcc.rides.push(rideRating.quiet);
      newAcc.rollingResistances.push(stopping.rollingResistanceRating);
      newAcc.comforts.push(rideRating.comfort);

      newAcc.mileages.push({ median: mileage.median, min: mileage.min });
      newAcc.tireLives.push({
        months: tireLifeMonths,
        years: tireLifeYears
      });
      newAcc.milesPerDollar.push(milesPerDollar);
      newAcc.wetStoppingDistances.push({
        rating: stopping.wetRating,
        stoppingDistance: stopping.wetDistance,
        wornStoppingDistance: stopping.wetWornDistance
      });
      newAcc.dryStoppingDistances.push({
        rating: stopping.dryRating,
        stoppingDistance: stopping.dryDistance
      });
      newAcc.winterStoppingDistances.push({
        rating: stopping.winterRating
      });
      newAcc.snowAccelerations.push(winter?.snowAccelerationRating);
      newAcc.snowCorneringGrips.push(winter?.snowLateralGRating);
      newAcc.snowSkidControls.push(winter?.snowHandlingRating);
      newAcc.snowStoppingDistances.push({
        rating: winter?.snowBrakeRating,
        stoppingDistance: winter?.snowBrakeFeet
      });

      newAcc.overallReviewRatings.push({
        count: getNumberOfProductReviews(product),
        rating: getAverageReviewRating(product),
        recommendCount: product.reviewSummaryData?.recommendCount
      });

      const reviewKeyToValue =
        reviews[idx]?.averageRatings?.reduce(
          (acc, review) => ({
            ...acc,
            [review.key]: Number(review.value)
          }),
          {}
        ) || {};
      newAcc.rideComfortReviewRatings.push(reviewKeyToValue[RIDECOMFORT_KEY]);
      newAcc.corneringReviewRatings.push(
        reviewKeyToValue[CORNERINGSTEERING_KEY]
      );
      newAcc.rideNoiseReviewRatings.push(reviewKeyToValue[RIDENOISE_KEY]);
      newAcc.treadLifeReviewRatings.push(reviewKeyToValue[TREADLIFE_KEY]);
      newAcc.dryTractionReviewRatings.push(reviewKeyToValue[DRYTRACTION_KEY]);
      newAcc.wetTractionReviewRatings.push(reviewKeyToValue[WETTRACTION_KEY]);
      newAcc.winterTractionReviewRatings.push(
        reviewKeyToValue[WINTERWEATHER_KEY]
      );
      newAcc.recommendedPercentages.push(reviews[idx]?.recommendedPercentage);
      newAcc.readReviewLinks.push(
        `${product.url}${
          isStaggeredSearch ? '/rearProduct/' + rearProduct.code : ''
        }#read-reviews`
      );

      newAcc.products.push(product);

      const { totalCost } = product?.recommendationData || {};
      const costPerMile = getTireCostPerMile(product);
      newAcc.costPerMile.push(totalCost && costPerMile);

      return newAcc;
    },
    {
      annualOwnershipCost: [],
      aspectRatios: [],
      comforts: [],
      corneringReviewRatings: [],
      costPerMile: [],

      diameters: [],
      dryStoppingDistances: [],
      dryTractionReviewRatings: [],
      gbbs: [],
      handlings: [],
      icons: [],
      loadIndexes: [],
      loadRanges: [],
      maxLoads: [],
      maxPSIs: [],

      mileages: [],
      milesPerDollar: [],
      overallReviewRatings: [],
      productNums: [],
      productWeight: [],

      products: [],
      readReviewLinks: [],
      recommendedPercentages: [],

      rideComfortReviewRatings: [],
      rideNoiseReviewRatings: [],
      rides: [],
      rimWidthRanges: [],
      rollingResistances: [],

      sectionWidths: [],
      sideWallDescriptions: [],
      snowAccelerations: [],
      snowCorneringGrips: [],
      snowSkidControls: [],
      snowStoppingDistances: [],
      speedRatings: [],
      tempGrades: [],

      tireLives: [],
      tractionGrades: [],
      treadDepths: [],
      treadGrades: [],
      treadLifeReviewRatings: [],
      treadwellWarranties: [],
      warranties: [],
      wetHandlings: [],
      wetStoppingDistances: [],
      wetTractionReviewRatings: [],
      wheelDiameters: [],
      winterStoppingDistances: [],

      winterTractionReviewRatings: []
    }
  );
}

function useFetchCompareProducts({
  initialQuantities = {},
  maxProductsForComparison,
  productsForComparison,
  selectedFitment,
  treadwellPreferences
}) {
  const { myStore, nearbyStorePreferences } = useMyStore();

  const filteredNearByStoreCodes = useMemo(() => {
    const nearByStores = myStore?.nearByStores;
    return nearByStores
      ?.filter(storeData => nearbyStorePreferences?.includes(storeData.code))
      .map(storeData => storeData.code);
  }, [nearbyStorePreferences, myStore]);

  //TODO - Swap with real treadwell data once implemented
  const milesPerYear = 15;

  const { isFleetAccount } = useMyAccount();
  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const { productCodes, productCodesWithContext, staggeredProductCodes } =
    productsForComparison.reduce(
      (obj, product) => {
        const treadwellSelectorType = getTreadwellSelectorType(
          product?.winterToggleStatus
        );

        obj.productCodes.push(product?.code);
        obj.staggeredProductCodes.push({
          frontProductCode: product?.code,
          rearProductCode: product?.rearProduct?.code
        });
        obj.productCodesWithContext.push({
          compareSelectorType: product?.recommendationData
            ? treadwellSelectorType
            : PRODUCT_COMPARE_SELECTOR_TYPE.ALL_TIRES,
          frontProductCode: product?.code,
          rearProductCode: product?.rearProduct?.code
        });

        return obj;
      },
      {
        productCodes: [],
        productCodesWithContext: [],
        staggeredProductCodes: []
      }
    );

  const isStaggeredSearch = Boolean(
    staggeredProductCodes.find(
      productCodes => typeof productCodes.rearProductCode === 'string'
    )
  );

  const bundleType = isStaggeredSearch ? BundleType.StaggeredSet : null;

  const initialProductQty1 =
    initialQuantities[staggeredProductCodes[0]?.frontProductCode] || {};
  const initialProductQty2 =
    initialQuantities[staggeredProductCodes[1]?.frontProductCode] || {};
  const initialProductQty3 =
    initialQuantities[staggeredProductCodes[2]?.frontProductCode] || {};
  const initialProductQty4 =
    initialQuantities[staggeredProductCodes[3]?.frontProductCode] || {};

  const productQuantity1 = useProductQuantity({
    articleServiceType: productsForComparison?.[0]?.articleServiceType,
    bundleType,
    initialQuantity: initialProductQty1.quantity,
    initialRearQuantity: initialProductQty1.rearQuantity
  });
  const productQuantity2 = useProductQuantity({
    articleServiceType: productsForComparison?.[1]?.articleServiceType,
    bundleType,
    initialQuantity: initialProductQty2.quantity,
    initialRearQuantity: initialProductQty2.rearQuantity
  });
  const productQuantity3 = useProductQuantity({
    articleServiceType: productsForComparison?.[2]?.articleServiceType,
    bundleType,
    initialQuantity: initialProductQty3.quantity,
    initialRearQuantity: initialProductQty3.rearQuantity
  });
  const productQuantity4 = useProductQuantity({
    articleServiceType: productsForComparison?.[3]?.articleServiceType,
    bundleType,
    initialQuantity: initialProductQty4.quantity,
    initialRearQuantity: initialProductQty4.rearQuantity
  });
  const productQuantities = [
    productQuantity1,
    productQuantity2,
    productQuantity3,
    productQuantity4
  ];

  const treadwellRequest = getTreadwellGQLInput(
    selectedFitment,
    treadwellPreferences,
    isFleetTreadwell
  );
  const zipCode =
    treadwellRequest?.zipCode ||
    myStore?.address?.postalCode?.replace(/[^0-9].+$/, '');

  const shouldUseStandardSearchQuery = !isStaggeredSearch;

  const { data, isLoading } = useQuery(
    shouldUseStandardSearchQuery
      ? COMPARE_STANDARD_PRODUCT_QUERY
      : COMPARE_STAGGERED_PRODUCT_QUERY,
    {
      skip: !isArrayWithLength(productCodes),
      variables: {
        compareSelectorProductCodes: productCodesWithContext,
        nearByStoreCodes: filteredNearByStoreCodes,
        productCodes: shouldUseStandardSearchQuery
          ? productCodes
          : staggeredProductCodes.map(codes => ({
              front: codes.frontProductCode,
              rear: codes.rearProductCode
            })),
        storeCode: myStore?.code,
        treadwellRequest: treadwellRequest || {
          drivingStyle: TREADWELL_DEFAULT_PERSONA_NAME,
          fuelType: 'ICE',
          isOESize: true,
          isWinterSearch: false,
          milesPerYear,
          priority1: 'LIFE_OF_TIRE',
          priority2: 'HANDLING',
          priority3: 'COMFORT_AND_NOISE',
          priority4: 'STOPPING_DISTANCE',
          priority5: 'FUEL_EFFICIENCY',
          quantity: 4,
          zipCode
        },
        vehicleInfo: getVehicleInput(selectedFitment),
        zipCode
      }
    }
  );

  const { precipitationDaysPerYear, winterDaysPerYear } =
    data?.recommendationQueries?.weatherDetails || {};

  const productsInResponse = shouldUseStandardSearchQuery
    ? data?.product?.byCodesStandard
    : data?.product?.byCodes;
  const productReviews = shouldUseStandardSearchQuery
    ? data?.product?.averageRatingsStandard
    : data?.product?.averageRatings;
  const dryDaysPerYear = precipitationDaysPerYear
    ? 365 - precipitationDaysPerYear
    : 0;

  const { doesFitEnumMap } = useDoesProductFit({
    fitment: selectedFitment,
    isStaggered: isStaggeredSearch,
    productCodes: staggeredProductCodes.reduce((acc, productCodeSet) => {
      if (!productCodeSet) {
        return acc;
      }

      if (productCodeSet.rearProductCode) {
        acc.push({
          front: productCodeSet.frontProductCode,
          rear: productCodeSet.rearProductCode
        });
      } else {
        acc.push(productCodeSet.frontProductCode);
      }

      return acc;
    }, [])
  });

  const doAllCompareProductsFit =
    doesFitEnumMap &&
    Object.values(doesFitEnumMap)?.every(
      doesFit => doesFit === FIT || doesFit === EXCLUDED
    )
      ? FIT
      : DONT_FIT;

  // Display as much data as we can while we continue to fetch remaining data
  const products = isLoading ? productsForComparison : productsInResponse;

  const {
    hasTopRecommendedProductInCompareList,
    hasWinterTire,
    topRecommendedProduct
  } = useMemo(
    () =>
      products?.reduce(
        (acc, product) => {
          const topRecommendedProductData =
            product?.recommendationData?.topRecommendedProductData ||
            product?.tdsData?.topRecommendedProductData;

          if (topRecommendedProductData) {
            acc.topRecommendedProduct = topRecommendedProductData;
          }
          if (product?.recommendationData?.rank === 1) {
            acc.hasTopRecommendedProductInCompareList = true;
          }
          if (product?.recommendationData?.winter || product?.tdsData?.winter) {
            acc.hasWinterTire = true;
          }

          return acc;
        },
        {
          hasTopRecommendedProductInCompareList: false,
          hasWinterTire: false,
          topRecommendedProduct: null
        }
      ) || {},
    [products]
  );

  return {
    doAllCompareProductsFit,
    hasWinterTire,
    isLoading,
    productData: {
      ...filterProductData(
        products || [],
        productReviews || [],
        maxProductsForComparison,
        isStaggeredSearch
      ),
      dryDaysPerYear,
      isStaggered: isStaggeredSearch,
      milesPerYear,
      precipitationDaysPerYear,
      productQuantities,
      winterDaysPerYear
    },
    showCompareWithTreadwellNumberOneMessage:
      maxProductsForComparison > 2 &&
      !hasWinterTire &&
      !treadwellPreferences?.showWinterTires &&
      topRecommendedProduct &&
      !hasTopRecommendedProductInCompareList,
    topRecommendedProduct
  };
}

export default useFetchCompareProducts;
